import type { MaintenanceRecord } from '../../types';
import { DateFormat } from '../../utils/DateFormat';
import {
  costSplitRows,
  costSplitTitle,
  formatRub,
  resolveCostSplit,
} from '../../utils/maintenanceCost';
import {
  maintenanceReasonLabel,
  recordTitle,
} from '../../utils/maintenanceRecord';
import RecordDetailsModal from '../RecordDetailsModal/RecordDetailsModal';

type Props = {
  record: MaintenanceRecord;
  number?: number;
  canEdit?: boolean;
  canDelete?: boolean;
  onClose: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
};

const afterFlightValue = (record: MaintenanceRecord) => {
  if (record.after_flight_id && record.after_flight_date) {
    return `${DateFormat(String(record.after_flight_date))}${
      record.after_flight_pilot ? ` · ${record.after_flight_pilot}` : ''
    }`;
  }
  if (record.hobbs > 0) return 'Не привязан — выберите полёт';
  return 'Без полёта';
};

const MaintenanceDetailsModal = ({
  record,
  number,
  canEdit = false,
  canDelete = false,
  onClose,
  onEdit,
  onDelete,
}: Props) => {
  const title = recordTitle(record);
  const split = resolveCostSplit(record);
  const hasCost = record.cost != null && record.cost > 0;
  const splitRows = hasCost ? costSplitRows(record) : [];
  const reason = maintenanceReasonLabel(record.reason);

  return (
    <RecordDetailsModal
      title={title}
      summary={
        <>
          <span className="record-details-date">
            {DateFormat(String(record.date))}
          </span>
          <span className="record-details-lead">№{number ?? '—'}</span>
        </>
      }
      rows={[
        { label: 'Дата', value: DateFormat(String(record.date)) },
        { label: 'Наработка', value: `${record.hobbs} м/ч` },
        { label: 'После полёта', value: afterFlightValue(record) },
        { label: 'Кем проведено', value: record.mechanic || '—' },
        ...(reason ? [{ label: 'Причина', value: reason }] : []),
        {
          label: 'Стоимость',
          value: hasCost ? formatRub(record.cost as number) : '—',
        },
        {
          label: 'Распределение',
          value: hasCost ? costSplitTitle(split) : '—',
        },
      ]}
      extra={
        <>
          {hasCost && splitRows.length > 0 ? (
            <ul className="record-details-split">
              {splitRows.map((row) => (
                <li key={row.key}>
                  <span>{row.name}</span>
                  <span>
                    {row.detail}
                    {row.cost != null ? ` · ${formatRub(row.cost)}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          ) : hasCost ? (
            <p className="record-details-hint">
              {split === 'shares' || split === 'manual'
                ? 'Нет долей на дату ТО'
                : 'Нет налёта за период'}
            </p>
          ) : null}
          {canEdit && !canDelete ? (
            <p className="record-details-hint">
              Удалить можно только последнюю запись ТО. Перепривязать к полёту
              можно любую.
            </p>
          ) : null}
        </>
      }
      note={record.info}
      noteLabel="Инфо"
      actions={[
        ...(canEdit && onEdit ? [{ label: 'Изменить', onClick: onEdit }] : []),
        ...(canDelete && onDelete
          ? [{ label: 'Удалить', onClick: onDelete, danger: true }]
          : []),
      ]}
      closeLabel="Отмена"
      onClose={onClose}
    />
  );
};

export default MaintenanceDetailsModal;
